import { Logo } from '@beydesign/storybook';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Navigate, useLocation } from 'react-router';

import { paths } from '@/config/paths';
import { syncHubSpotTracking } from '@/lib/marketing-tags';
import { useAuthStore } from '@/stores/auth-store';
import { useOrgStore } from '@/stores/org-store';

import { identifyUser, resetAnalytics } from './analytics';
import { firebaseAuth, initializeAuthObserver } from './firebase';

const FullScreenLoader = () => (
  <div className="flex h-screen w-screen flex-col items-center justify-center">
    <div className="animate-pulse">
      <Logo />
    </div>
  </div>
);

export const AuthLoader = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const setFirebaseUser = useAuthStore((s) => s.setFirebaseUser);
  const setIsAdmin = useAuthStore((s) => s.setIsAdmin);
  const setIsLoading = useAuthStore((s) => s.setIsLoading);
  const isLoading = useAuthStore((s) => s.isLoading);

  React.useEffect(() => {
    const unsubscribe = initializeAuthObserver(async (user) => {
      setFirebaseUser(user);

      if (user) {
        syncHubSpotTracking(user.email);

        try {
          const tokenResult = await user.getIdTokenResult();
          setIsAdmin(tokenResult.claims.admin === true);
        } catch {
          setIsAdmin(false);
        }

        if (user.emailVerified) {
          identifyUser(user);
        }
      } else {
        setIsAdmin(false);
        resetAnalytics();
        queryClient.clear();
        useOrgStore.getState().clearOrg();
      }

      setIsLoading(false);
    });
    return unsubscribe;
  }, [queryClient, setFirebaseUser, setIsAdmin, setIsLoading]);

  if (isLoading) {
    return <FullScreenLoader />;
  }

  return <>{children}</>;
};

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const isLoading = useAuthStore((s) => s.isLoading);
  const location = useLocation();

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!firebaseUser) {
    return (
      <Navigate to={paths.auth.login.getHref(location.pathname)} replace />
    );
  }

  return <>{children}</>;
};

export const getIdToken = async (): Promise<string | null> => {
  const user = firebaseAuth.currentUser;
  if (!user) return null;
  return user.getIdToken();
};
